import { IconButton, RotateCcwIcon } from "@carbon6/c6-frontend-design";
import type { AxisFilterChip, AxisFilterFieldDef } from "./AxisFilterBar";
import { FilterResultChip } from "./FilterResultChip";
import styles from "./AxisFilterChips.module.css";

// 축 칩이 아닌 조건(기간 프리셋 등)을 같은 행에 추가하는 항목이다. 호출부가 문구와 해제를 소유한다.
export type AxisFilterLeadingChip = {
  key: string;
  label: string;
  removeLabel: string;
  onRemove: () => void;
};

/**
 * AxisFilterBar 로 확정한 조회 조건의 칩 행이다. 칩 ✕ 는 그 축만 해제한다. 행 끝의 [↺ 초기화] 는
 * 전부 해제한다. rules.md §17 에 따라 칩은 FilterResultChip 한 종류이고, 초기화는 칩이 있을 때만 표시한다.
 *
 * 입력 줄(AxisFilterBar)과 같은 fields/chips/onChange 를 받지만 입력은 가지지 않는다. 호출부가
 * 입력 줄과 칩 줄을 서로 다른 줄에 둘 수 있도록 두 컴포넌트로 나눈다. 축 밖의 조건(등록일
 * 프리셋)은 leadingChips 로 앞에 표시한다. 그런 조건이 있는 호출부는 onClearAll 로 초기화 범위를
 * 넓힌다. 칩이 하나도 없으면 줄을 만들지 않는다.
 */
export function AxisFilterChips<K extends string>({
  fields,
  chips,
  onChange,
  leadingChips = [],
  onClearAll,
}: {
  fields: readonly AxisFilterFieldDef<K>[];
  chips: readonly AxisFilterChip<K>[];
  onChange: (next: readonly AxisFilterChip<K>[]) => void;
  leadingChips?: readonly AxisFilterLeadingChip[];
  // 초기화가 비울 범위다. 생략하면 축 칩만(onChange([])) 비운다.
  onClearAll?: () => void;
}) {
  if (chips.length === 0 && leadingChips.length === 0) {
    return null;
  }

  const labelOf = (key: K) => fields.find((candidate) => candidate.key === key)?.label ?? key;

  return (
    <div className={styles.chips} role="group" aria-label="적용된 Filter">
      {leadingChips.map((chip) => (
        <FilterResultChip
          key={`leading:${chip.key}`}
          label={chip.label}
          removeLabel={chip.removeLabel}
          onRemove={chip.onRemove}
        />
      ))}
      {chips.map((chip) => {
        const label = `${labelOf(chip.key)}: ${chip.value}`;
        return (
          <FilterResultChip
            key={chip.key}
            label={label}
            removeLabel={`${label} 해제`}
            onRemove={() => onChange(chips.filter((candidate) => candidate.key !== chip.key))}
          />
        );
      })}
      <IconButton
        text="초기화"
        type="button"
        size="sm"
        variant="ghost"
        className={styles.clearAll}
        onClick={() => (onClearAll ?? (() => onChange([])))()}
      >
        <RotateCcwIcon />
      </IconButton>
    </div>
  );
}
